<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between mb-4 page-header">
      <v-card-title class="text-h5 font-weight-bold pa-0">
        <v-icon left color="#4a3aff">mdi-account-clock</v-icon>
        {{ $t('admin.accessRequests.title') }}
      </v-card-title>

      <v-btn color="primary" :loading="loading" @click="loadRequests">
        <v-icon left>mdi-refresh</v-icon>
        {{ $t('common.refresh') }}
      </v-btn>
    </div>

    <v-alert v-if="message" :type="messageType" dense border="left" class="mb-4">
      {{ message }}
    </v-alert>

    <v-card class="pa-5 rounded-lg">
      <v-data-table
        :headers="headers"
        :items="requests"
        :loading="loading"
        sort-by="createdAtSort"
        :sort-desc="true"
        class="elevation-0"
      >
        <template #[`item.contact`]="{ item }">
          <div class="font-weight-medium">{{ item.name || $t('common.noName') }}</div>
          <div class="text-caption grey--text text--darken-1">{{ item.email }}</div>
          <div class="text-caption grey--text text--darken-1">{{ item.phone }}</div>
        </template>

        <template #[`item.company`]="{ item }">
          <div class="font-weight-medium">{{ item.company || $t('common.noCompany') }}</div>
          <div class="text-caption grey--text text--darken-1">{{ item.address }}</div>
        </template>

        <template #[`item.notes`]="{ item }">
          <span class="notes-cell">{{ item.notes }}</span>
        </template>

        <template #[`item.accessMethod`]="{ item }">
          {{ accessMethodText(item.accessMethod) }}
        </template>

        <template #[`item.status`]="{ item }">
          <v-chip small :color="statusColor(item.status)">
            {{ statusText(item.status) }}
          </v-chip>
        </template>

        <template #[`item.createdAt`]="{ item }">
          {{ formatDateTime(item.createdAt) }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-menu offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="updateStatus(item, 'pending')">
                <v-list-item-title>{{ $t('statuses.pending') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="updateStatus(item, 'reviewed')">
                <v-list-item-title>{{ $t('statuses.reviewed') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="updateStatus(item, 'accepted')">
                <v-list-item-title>{{ $t('statuses.accepted') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="updateStatus(item, 'rejected')">
                <v-list-item-title>{{ $t('statuses.rejected') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template #no-data>
          {{ $t('admin.accessRequests.empty') }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { db } from "@/firebase";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { getUserErrorMessage } from "@/utils/errors";

export default {
  name: "AccessRequests",

  data() {
    return {
      loading: false,
      message: "",
      messageType: "success",
      requests: [],
    };
  },

  computed: {
    headers() {
      return [
        { text: this.$t("common.contact"), value: "contact" },
        { text: this.$t("common.company"), value: "company" },
        { text: this.$t("requestAccess.accessMethod"), value: "accessMethod" },
        { text: this.$t("common.notes"), value: "notes" },
        { text: this.$t("common.status"), value: "status" },
        { text: this.$t("common.date"), value: "createdAt" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ];
    },
  },

  async created() {
    await this.loadRequests();
  },

  methods: {
    async loadRequests() {
      this.loading = true;
      this.message = "";

      try {
        const snap = await getDocs(collection(db, "requests"));
        this.requests = snap.docs
          .map((requestDoc) => {
            const data = requestDoc.data();
            return {
              id: requestDoc.id,
              ...data,
              createdAtSort: this.toMillis(data.createdAt),
            };
          })
          .sort((a, b) => b.createdAtSort - a.createdAtSort);
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "admin.accessRequests.loadError", this.$i18n), "error");
      } finally {
        this.loading = false;
      }
    },

    async updateStatus(item, status) {
      if (item.status === status) return;

      try {
        await updateDoc(doc(db, "requests", item.id), { status });
        item.status = status;
        this.showMessage(this.$t("admin.accessRequests.updated"), "success");
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "admin.accessRequests.updateError", this.$i18n), "error");
      }
    },

    statusText(status) {
      return {
        pending: this.$t("statuses.pending"),
        reviewed: this.$t("statuses.reviewed"),
        accepted: this.$t("statuses.accepted"),
        rejected: this.$t("statuses.rejected"),
      }[status] || status || this.$t("statuses.pending");
    },

    statusColor(status) {
      return {
        pending: "orange lighten-4",
        reviewed: "blue lighten-4",
        accepted: "green lighten-4",
        rejected: "red lighten-4",
      }[status] || "grey lighten-3";
    },

    accessMethodText(accessMethod) {
      return {
        google: this.$t("requestAccess.accessMethodShortGoogle"),
        password: this.$t("requestAccess.accessMethodShortPassword"),
      }[accessMethod] || "-";
    },

    formatDateTime(value) {
      if (!value) return "";
      const date = value?.toDate ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return date.toLocaleString(this.$i18n.locale, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    toMillis(value) {
      if (!value) return 0;
      const date = value?.toDate ? value.toDate() : new Date(value);
      return Number.isNaN(date.getTime()) ? 0 : date.getTime();
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    },
  },
};
</script>

<style scoped>
.v-card {
  background-color: #f8f7fd;
}

.notes-cell {
  display: inline-block;
  max-width: 260px;
  white-space: normal;
}

::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

@media (max-width: 600px) {
  .page-header {
    align-items: stretch !important;
    flex-direction: column;
    gap: 12px;
  }

  .page-header .v-card__title {
    font-size: 1.2rem !important;
    line-height: 1.3;
  }

  .v-card {
    padding: 14px !important;
  }

  .notes-cell {
    max-width: 220px;
  }
}

</style>
